import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  Paper,
  IconButton,
  Breadcrumbs,
  Link as MuiLink,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextareaAutosize,
} from '@mui/material';
import {
  ArrowBack,
  ShoppingCart,
  Favorite,
  Share,
  Star,
  StarBorder,
  LocalShipping,
  Security,
  Support,
  Close,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useGetProductByIdQuery } from '../api/productDisplayApi';

interface ProductDetailProps {}

export const ProductDetail: React.FC<ProductDetailProps> = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  
  const { data: product, isLoading: loading, error: apiError } = useGetProductByIdQuery(productId || '', { skip: !productId });
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    quantity: 1,
    deliveryDate: '',
    specialRequirements: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
  });

  const error = apiError ? 'Failed to load product details' : null;

  const handleQuoteSubmit = async () => {
    try {
      // TODO: Implement quote submission API call
      console.log('Quote submitted:', { productId, ...quoteForm });
      setQuoteDialogOpen(false);
      // Show success message
    } catch (err) {
      console.error('Error submitting quote:', err);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Product not found'}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={handleBack}>
          Back to Products
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3, color: 'white' }}>
          <MuiLink color="inherit" href="/" underline="hover">
            Home
          </MuiLink>
          <MuiLink color="inherit" href="/products" underline="hover">
            Products
          </MuiLink>
          <Typography color="inherit">{product.name}</Typography>
        </Breadcrumbs>

        {/* Product Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Grid container spacing={4}>
            {/* Product Image */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  minHeight: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Typography variant="h6" color="text.secondary">
                    Product Image
                  </Typography>
                )}
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    background: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': { background: 'rgba(255, 255, 255, 1)' },
                  }}
                >
                  <Favorite />
                </IconButton>
              </Box>
            </Grid>

            {/* Product Info */}
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                {product.name}
              </Typography>
              
              <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                ${product.price}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} sx={{ color: '#ffc107', fontSize: 20 }} />
                ))}
                <Typography variant="body2" sx={{ ml: 1 }}>
                  (4.8/5) • 127 reviews
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {product.description}
              </Typography>

              {/* Product Category */}
              <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                <Chip
                  label={product.category}
                  variant="outlined"
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  }}
                />
              </Stack>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ShoppingCart />}
                  onClick={() => setQuoteDialogOpen(true)}
                  sx={{
                    px: 4,
                    py: 1.5,
                    background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                    },
                  }}
                >
                  Get Quote
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Share />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    border: '2px solid #6366f1',
                    color: '#6366f1',
                    '&:hover': {
                      border: '2px solid #4f46e5',
                      background: 'rgba(99, 102, 241, 0.04)',
                    },
                  }}
                >
                  Share
                </Button>
              </Stack>

              {/* Quick Info */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalShipping sx={{ color: 'primary.main' }} />
                  <Typography variant="body2">Free Shipping</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Security sx={{ color: 'primary.main' }} />
                  <Typography variant="body2">Secure Payment</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Support sx={{ color: 'primary.main' }} />
                  <Typography variant="body2">24/7 Support</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Product Details Tabs */}
        <Grid container spacing={4}>
          {/* Specifications */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Specifications
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {product.category}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Stock
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {product.stock} units
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Price
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      ${product.price}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {new Date(product.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Related Products */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Related Products
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Similar products you might be interested in
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Quote Request Dialog */}
      <Dialog
        open={quoteDialogOpen}
        onClose={() => setQuoteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Request Quote for {product.name}
          <IconButton
            onClick={() => setQuoteDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={quoteForm.quantity}
                onChange={(e) => setQuoteForm({ ...quoteForm, quantity: parseInt(e.target.value) })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Delivery Date"
                type="date"
                value={quoteForm.deliveryDate}
                onChange={(e) => setQuoteForm({ ...quoteForm, deliveryDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Name"
                value={quoteForm.contactName}
                onChange={(e) => setQuoteForm({ ...quoteForm, contactName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={quoteForm.contactEmail}
                onChange={(e) => setQuoteForm({ ...quoteForm, contactEmail: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={quoteForm.contactPhone}
                onChange={(e) => setQuoteForm({ ...quoteForm, contactPhone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Special Requirements"
                multiline
                rows={4}
                value={quoteForm.specialRequirements}
                onChange={(e) => setQuoteForm({ ...quoteForm, specialRequirements: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleQuoteSubmit}
            sx={{
              background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
              },
            }}
          >
            Submit Quote Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 